import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { trasladosService } from '../../../api/trasladosService'
import type { Traslado, TrasladosFilters } from '../../../types/traslados'
import { extractAxiosApiErrorMessage } from '../../../utils/apiErrorMessage'

const DEFAULT_LIMIT = 20

export function useTraslados(initialEstados: string[] = []) {
  const [traslados, setTraslados] = useState<Traslado[]>([])
  const [estados, setEstados] = useState<string[]>(initialEstados)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchTraslados = useCallback(async (filters?: Partial<TrasladosFilters>) => {
    setIsLoading(true)
    setError(null)

    const estadoFilter = filters?.estado ?? (estados.length > 0 ? estados : undefined)

    const query: TrasladosFilters = {
      estado: estadoFilter,
      page: filters?.page ?? page,
      limit: filters?.limit ?? limit
    }

    try {
      const response = await trasladosService.getTraslados(query)
      const rows = Array.isArray(response.data) ? response.data : []
      setTraslados(rows)
      setTotal(response.pagination?.total ?? rows.length)
    } catch (err) {
      const message = extractAxiosApiErrorMessage(err, 'Error al cargar transferencias')
      setError(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }, [estados, limit, page])

  useEffect(() => {
    fetchTraslados()
  }, [fetchTraslados])

  const totalPages = Math.max(1, Math.ceil(total / limit))

  return {
    traslados,
    estados,
    setEstados,
    page,
    setPage,
    limit,
    setLimit,
    total,
    totalPages,
    isLoading,
    error,
    refetch: fetchTraslados
  }
}

export function useTrasladoActions(onCompleted?: () => void) {
  const [actionLoading, setActionLoading] = useState(false)
  const [retryingId, setRetryingId] = useState<number | null>(null)

  const anular = useCallback(async (id: number) => {
    setActionLoading(true)
    try {
      await trasladosService.anularTraslado(id)
      toast.success('Transferencia anulada')
      onCompleted?.()
    } catch (error) {
      toast.error(extractAxiosApiErrorMessage(error, 'Error al anular la transferencia'))
    } finally {
      setActionLoading(false)
    }
  }, [onCompleted])

  const reenviarFlexy = useCallback(async (id: number, force = false) => {
    setActionLoading(true)
    setRetryingId(id)
    try {
      await trasladosService.reenviarFlexy(id, force)
      toast.success(force ? 'Reenvío forzado a Flexy' : 'Reintento enviado a Flexy')
      onCompleted?.()
    } catch (error) {
      toast.error(extractAxiosApiErrorMessage(error, 'Error al reenviar a Flexy'))
    } finally {
      setActionLoading(false)
      setRetryingId(null)
    }
  }, [onCompleted])

  return { actionLoading, retryingId, anular, reenviarFlexy }
}
